(ns manage.ad-variant.creation.feed.mapping.util
  (:require [clojure.set :refer [rename-keys]]
            [clojure.string :as str]
            [mate.core :as mc]))

;; ============================================================================
;; Child Mapping Utilities
;; ============================================================================
;; These utilities handle the conversion between form state and API format
;; for collection child placeholders.
;;
;; Key concept: The mapping uses NESTED structure for form and API/BE:
;; - Form: {:placeholder-name {:value "field-name" :child-mapping {...}}}
;; - API: {product-store-field {name child-placeholder type text}}

(defn- tokenize-string
  "Tokenize a string by splitting on _, -, or space."
  [s]
  (str/split s #"_|-| "))

(defn automap-items
  [placeholders fields]
  (let [fields-lowercase (map #(vector (str/lower-case %) %) fields)
        fields-map (into {} fields-lowercase)
        tokenized-fields-map (into {} (map #(vector (tokenize-string (str/lower-case (first %))) (second %)) fields-lowercase))]
    (->> placeholders
         (map (fn [{:keys [name] :as placeholder}]
                (let [lower-name (str/lower-case name)
                      tokenized-name (tokenize-string lower-name)]
                  (if (contains? fields-map lower-name)
                    ;; let's try exact match first
                    (assoc placeholder :value (get fields-map lower-name))
                    ;; and fallback to the tokenized match
                    (when (contains? tokenized-fields-map tokenized-name)
                      (assoc placeholder :value (get tokenized-fields-map tokenized-name)))))))
         (remove nil?))))

(defn get-placeholders-from-revision
  [revision]
  (->> (:placeholders revision)
       (mapv (fn [[key-name {:keys [type intent placeholders items_required]}]]
               {:name (name key-name) :type type :intent intent :placeholders placeholders :items_required items_required}))))

(defn get-placeholders
  [revisions]
  (->> revisions
       (mapcat get-placeholders-from-revision)
       distinct
       vec))

(def strategy-class "targeting-key")

(def identifier
  [{:name "Identifier"
    :type "identifier"
    :tooltip "A unique identifier to distinguish each variant."}])

(def template-dimensions
  [{:name "Dimensions"
    :type "template-dimensions"
    :tooltip "Used to map content to specific dimensions in the template (e.g., banner size or format)."}])

(def click-through-item
  {:name "Click"
   :type "click-through"})

(def meta-items
  (let [read-more-props {:tooltip-read-more-cta "Meta docs"
                         :tooltip-read-more-url "https://www.facebook.com/business/ads-guide/update/image"}]
    [click-through-item
     (merge {:name "Meta Headline"
             :type "meta-headline"
             :tooltip "Recommended maximum length: 27 characters."}
            read-more-props)
     (merge {:name "Meta CTA"
             :type "meta-cta"
             :tooltip "Represents the action text for the ad (e.g., \"Shop Now,\" \"Learn More\")."}
            read-more-props)
     (merge {:name "Meta Display Link"
             :type "meta-cta-caption"
             :tooltip "Represents the shortened URL for the ad."}
            read-more-props)
     (merge {:name "Meta Description"
             :type "meta-description"
             :tooltip "Recommended maximum length: 25 characters."}
            read-more-props)
     (merge {:name "Meta Primary Text"
             :type "meta-primary-text"
             :tooltip "Recommended maximum length: 50-150 characters."}
            read-more-props)]))

(def tiktok-items
  [click-through-item
   {:name "TikTok Ad Name"
    :type "tiktok-ad-name"}
   {:name "TikTok Ad Text"
    :type "tiktok-ad-text"}
   {:name "TikTok CTA"
    :type "tiktok-cta"}])

(def pass-through-name
  "Pass Through URL")

(def pass-through-thumb-name
  "Pass Through Thumbnail")

(def pass-through-items
  [{:name pass-through-name
    :type "pass-through-url"
    :tooltip "Video to use for meta ad."}
   {:name pass-through-thumb-name
    :type "pass-through-thumbnail"
    :tooltip "Thumbnail to use for meta ad."}])

(def youtube-items
  [click-through-item
   {:name "YouTube Description"
    :type "youtube-description"
    :tooltip "Maximum length: 5000 characters."
    :tooltip-read-more-cta "YouTube docs"
    :tooltip-read-more-url "https://support.google.com/youtube/answer/12948449?hl=en"}])

(def online-video-items
  [click-through-item])

(def connected-tv-avod-items
  [click-through-item])

(def connected-tv-slate-items
  [{:name "SLATE_DATE"
    :tooltip "Example Value: 28-01-2025"
    :type "content"}
   {:name "SLATE_AUDIO"
    :tooltip "Example Value: Stereo"
    :type "content"}
   {:name "SLATE_RATIO"
    :tooltip "Example Value: FHD"
    :type "content"}
   {:name "SLATE_DURATION"
    :tooltip "Example Value: 15"
    :type "content"}
   {:name "SLATE_CLOCKNUMBER"
    :tooltip "Example Value: FCS/BEAR001/015"
    :type "content"}
   {:name "SLATE_AGENCY"
    :tooltip "Example Value: GroupM Nexus Media Solutions UK"
    :type "content"}
   {:name "SLATE_CLIENTNAME"
    :tooltip "Example Value: Lotus Bakeries"
    :type "content"}
   {:name "SLATE_VERSION"
    :tooltip "Example Value: 10 second countdown"
    :type "content"}
   {:name "SLATE_TITLE"
    :tooltip "Example Value: Lotus Bakeries - Bear - Jan 25"
    :type "content"}])

(def connected-tv-bvod-items
  (into [click-through-item
         {:name "Clock number"
          :tooltip "A unique ID for material submitted to ClearCast."
          :type "clear-cast-id"}]
        connected-tv-slate-items))

(def scheduling-items
  [{:name "Timezone" :type "scheduling-timezone"}
   {:name "Start date" :type "scheduling-start"}
   {:name "End date" :type "scheduling-end"}])

(def field-classes-by-name
  (->>
   (concat identifier template-dimensions meta-items tiktok-items youtube-items online-video-items connected-tv-bvod-items scheduling-items)
   (into {} (map (juxt :name :type)))))

(def field-classes-by-type
  (->>
   (concat identifier template-dimensions meta-items tiktok-items youtube-items online-video-items connected-tv-bvod-items scheduling-items)
   (into {} (map (juxt :type :name)))))

(def slate-items->placeholders
  (map (fn [item] {:name (:name item) :type "text"}) connected-tv-slate-items))

(defn create-field-classes
  [mappings template-placeholders existing-field-classes]
  (let [name->placeholder (mc/index-by :name template-placeholders)
        strategy-assignments (filter (comp #{strategy-class} :class) existing-field-classes)
        ;; Extract feed columns used in child mappings
        feed-columns-from-children (into []
                                         (comp (mapcat (fn [[_mapping-name mapping-value]]
                                                         (when (and (map? mapping-value) (:child-mapping mapping-value))
                                                           (vals (:child-mapping mapping-value)))))
                                               (filter (fn [child-value]
                                                         (and (map? child-value)
                                                              (:source-column child-value)
                                                              (= "feed" (:type child-value)))))
                                               (map (fn [child-value]
                                                      {:name (:source-column child-value) :class "content-key"})))
                                         mappings)]
    (->> mappings
         (mapv (fn [[mapping-name field-value]]
                 (let [field-name (if (map? field-value) (:value field-value) field-value)
                       placeholder-name-str (name mapping-name)
                       placeholder-type (get-in name->placeholder [placeholder-name-str :type])
                       placeholder-intent (get-in name->placeholder [placeholder-name-str :intent])
                       field-class-key (cond
                                         (= "click" placeholder-type) "click-through-key"

                                         (get field-classes-by-name placeholder-name-str)
                                         (str (get field-classes-by-name placeholder-name-str) "-key")

                                         (= "product" placeholder-intent) "dynamic-content-key"
                                         :else "content-key")]
                   (when (some? field-name)
                     {:name field-name :class field-class-key}))))
         (concat strategy-assignments feed-columns-from-children)
         (remove nil?)
         set
         vec)))

(defn- build-child-mapping
  "Build the inverted child-mapping for a collection placeholder.
   Converts child values to product-store-field keys with placeholder configs."
  [placeholders child-mapping-values]
  (->> placeholders
       (reduce-kv (fn [acc k {:keys [type]}]
                    (let [child-placeholder-name (name k)
                          child-value (or (get child-mapping-values child-placeholder-name)
                                          (get child-mapping-values (keyword child-placeholder-name)))
                          {:keys [value type-override source-column]} (when (map? child-value)
                                                                        {:value (:value child-value)
                                                                         :type-override (some-> (:type child-value) keyword)
                                                                         :source-column (:source-column child-value)})
                          field-name (if (map? child-value) value child-value)
                          child-type (or type-override :product)]
                      (if (some? field-name)
                        (let [base-config {:name child-placeholder-name :type type}
                              config (if (= :feed child-type)
                                       (assoc base-config :source-column source-column)
                                       base-config)]
                          (assoc acc (keyword field-name) config))
                        acc)))
                  {})))

(defn create-content-mappings
  [mappings template-placeholders]
  ;; input shape: {:placeholder-name {:value "field-name"} or {:value "field-name" :child-mapping {...}}}
  ;; output shape: [{"name": "cta_text", "placeholder": { "type": "text" "name": "cta_text" }}]
  (let [name->placeholder (mc/index-by :name template-placeholders)]
    (->> mappings
         (mapv (fn [[placeholder-key mapping-value]]
                 (let [placeholder-name (name placeholder-key)
                       placeholder-info (get name->placeholder placeholder-name)
                       placeholder-type (:type placeholder-info)
                       placeholder-intent (:intent placeholder-info)
                       actual-field-name (if (map? mapping-value) (:value mapping-value) mapping-value)
                       placeholder-definition (cond-> {:name placeholder-name :type placeholder-type}
                                                (= "collection" placeholder-type)
                                                (assoc :child-mapping
                                                       (build-child-mapping (:placeholders placeholder-info)
                                                                            (:child-mapping mapping-value))
                                                       :items-required (get placeholder-info :items_required 10)))]
                   (when (and (some? placeholder-type) (some? actual-field-name))
                     (cond-> {:name actual-field-name :placeholder placeholder-definition}
                       (= "product" placeholder-intent)
                       (assoc :dynamic-content {:type "products" :select-by "id" :alias actual-field-name}))))))
         (remove nil?))))

(defn ad-format->product-collection-placeholder [{:keys [current-revision]}]
  (keep (fn [[placeholder-name {:keys [type intent]}]]
          (when (and (= "collection" type)
                     (= "product" intent))
            placeholder-name))
        (:placeholders current-revision)))

(defn ->ad-formats-with-products-dynamic-content-payload [ad-builders content-mapping]
  (let [placeholder-name->content-mapping (->> content-mapping
                                               (filter #(= "products" (get-in % [:dynamic-content :type])))
                                               (mc/index-by #(get-in % [:placeholder :name])))
        ad-formats-fn (fn [ad-formats]
                        (mapv (fn [ad-format]
                                (let [placeholders (ad-format->product-collection-placeholder ad-format)
                                      mappings (when (seq placeholders)
                                                 (keep #(placeholder-name->content-mapping (name %)) placeholders))]
                                  (if (seq mappings)
                                    ;; the current's template-revision-id is missing from the data returned by the backend
                                    ;; So we need to build it up
                                    (-> ad-format
                                        (assoc :template-revision-id (get-in ad-format [:current-revision :id]))
                                        (assoc-in [:dynamic-content :products] (reduce (fn [acc mapping]
                                                                                         (assoc acc
                                                                                                (get-in mapping [:placeholder :name])
                                                                                                {:source-column (:name mapping)
                                                                                                 :placeholder (:placeholder mapping)}))
                                                                                       {}
                                                                                       mappings))
                                        (dissoc :latest-revision :current-revision))
                                    ad-format)))
                              ad-formats))]
    (when (seq placeholder-name->content-mapping)
      (->> ad-builders
           (filter (fn [ad-builder]
                     (let [placeholder-name (some (comp first ad-format->product-collection-placeholder) (:ad-formats ad-builder))]
                       (when placeholder-name
                         (placeholder-name->content-mapping (name placeholder-name))))))
           (map (fn [ad-builder]
                  {:id (:id ad-builder)
                   :ad-formats (ad-formats-fn (:ad-formats ad-builder))}))))))

(defn field-classes->internal-mapping
  [field-classes is-virtual-click-supported?]
  ;; class without "-key" suffix is becoming key in internal mapping,
  ;; content-key and targeting-key are dropped,
  ;; click-through-key is dropped as well for non video channels.
  (let [keys-to-exclude (cond-> #{"content-key" "dynamic-content-key" "targeting-key" "pass-through-url" "pass-through-thumbnail"}
                          (not is-virtual-click-supported?) (conj "click-through-key"))]
    (->> field-classes
         (remove #(some? (keys-to-exclude (:class %))))
         (keep (fn [item]
                 (let [type (str/replace-first (:class item) #"-key$" "")
                       placeholder-name (get field-classes-by-type type)
                       field-name (:name item)]
                   (when placeholder-name
                     [placeholder-name {:value field-name}]))))
         (into {}))))

(defn add-pass-through-mapping
  [field-content-mappings mappings]
  (let [pass-through-field (get mappings pass-through-name)
        pass-through-thumb-field (get mappings pass-through-thumb-name)]
    (cond-> (vec field-content-mappings)
      pass-through-field (conj {:name pass-through-field :placeholder {:name "pass-through-url" :type "video"}})
      pass-through-thumb-field (conj {:name pass-through-thumb-field :placeholder {:name "pass-through-thumbnail" :type "image"}}))))

(defn add-click-mapping
  [field-content-mappings field-classes]
  ;; WPPODC-8833 This should be temporary fix. It probably feels weird to you because it is weird
  ;; Meta and YouTube adsets do not contain the clickthrough in the template, so we need to add it as a "virtual" placeholder.
  ;; However the backend seems to get confused by this at some point during the configuration flow, and responds with incomplete data.
  ;; This function overwrites that incomplete data and creates "valid" placeholder instead of it.
  (let [classes-by-type (into {} (map (juxt :class :name)) field-classes)
        click-field (get classes-by-type "click-through-key")]
    (cond-> (vec field-content-mappings)
      click-field (conj {:name click-field :placeholder {:name click-field :type "click"}}))))

(defn- api-child-mapping->nested
  "Convert API child-mapping format to nested form structure.

   API format: {product-field-name {name placeholder-name source-column feed-column}}

   Form format: {placeholder-name {:value product-field-name
                                   :type :product|:feed
                                   :source-column feed-column}}

   Rules:
   - If source-column exists: :type :feed, preserve product field in :value, feed column in :source-column
   - If no source-column: :type :product, product field in :value"
  [api-child-mapping]
  (->> api-child-mapping
       (map (fn [[product-field-name {source-column :source-column
                                      placeholder-name :name}]]
              [placeholder-name (if (some? source-column)
                                  {:value (clojure.core/name product-field-name)
                                   :type :feed
                                   :source-column source-column}
                                  {:value (clojure.core/name product-field-name)
                                   :type :product})]))
       (into {})))

(defn ->internal-mapping
  [field-classes field-content-mappings is-virtual-click-supported?]
  (let [field-classes-mapping (field-classes->internal-mapping field-classes is-virtual-click-supported?)
        field-mappings (if is-virtual-click-supported?
                         (remove #(= (-> % :placeholder :type) "click") field-content-mappings)
                         field-content-mappings)
        ;; Build nested structure from field-content-mappings
        nested-mappings (->> field-mappings
                             (map (fn [item]
                                    (let [placeholder-name (get-in item [:placeholder :name])
                                          placeholder-type (get-in item [:placeholder :type])
                                          field-name (:name item)
                                          mapping-value (if (= "collection" placeholder-type)
                                                          {:value field-name
                                                           :child-mapping
                                                           (api-child-mapping->nested
                                                            (get-in item [:placeholder :child-mapping]))}
                                                          {:value field-name})]
                                      [placeholder-name mapping-value])))
                             (remove (fn [[k _]] (nil? k)))
                             (into {}))]
    (-> nested-mappings
        (merge field-classes-mapping)
        ;; Handle the special case of pass-through-url field which have to be renamed
        (as-> m (rename-keys m {"pass-through-url" pass-through-name
                                "pass-through-thumbnail" pass-through-thumb-name})))))

(defn automap-child-placeholders
  "Auto-map child placeholders to product store fields by name.
   Uses the same matching strategy as automap-items:
   1. Try exact match (case-insensitive)
   2. Fallback to tokenized match (splits on _, -, or space)"
  [template-items product-store-fields]
  (let [;; Create lowercase map for exact matching
        field-names-lowercase (->> product-store-fields
                                   (map :name)
                                   (map #(vector (str/lower-case %) %))
                                   (into {}))
        ;; Create tokenized map for fallback matching
        tokenized-fields-map (->> product-store-fields
                                  (map :name)
                                  (map #(vector (tokenize-string (str/lower-case %)) %))
                                  (into {}))
        ;; Find all collection placeholders
        collection-items (->> template-items
                              (filter #(= "collection" (:type %)))
                              (filter #(some? (:placeholders %))))]
    (->> collection-items
         (map (fn [{collection-name :name placeholders :placeholders}]
                (when placeholders
                  (let [child-mappings
                        (->> placeholders
                             (map (fn [[child-key _child-config]]
                                    (let [child-name (name child-key)
                                          lower-child-name (str/lower-case child-name)
                                          tokenized-child-name (tokenize-string lower-child-name)]
                                      (if-let [matched-field (get field-names-lowercase lower-child-name)]
                                        [(name child-key) {:value matched-field :type :product}]
                                        (when-let [matched-field (get tokenized-fields-map tokenized-child-name)]
                                          [(name child-key) {:value matched-field :type :product}])))))
                             (remove nil?)
                             (into {}))]
                    (when (seq child-mappings)
                      [(keyword collection-name) {:value collection-name :child-mapping child-mappings}])))))
         (remove nil?)
         (into {}))))
